import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listEstimates } from './api';
const PAGE_LIMIT = 20;
const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Converted', value: 'converted' },
];
function statusBadgeStyle(status) {
    switch (status) {
        case 'accepted':
            return styles.badgeGreen;
        case 'rejected':
            return styles.badgeRed;
        case 'converted':
            return styles.badgeBlue;
        default:
            return styles.badgeGray;
    }
}
export const EstimateList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listEstimates({
                status: statusFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load estimates');
        }
        finally {
            setLoading(false);
        }
    }, [statusFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const handleTabChange = (value) => {
        setStatusFilter(value);
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = tokenStack.slice(0, next);
        newStack.push(result.nextToken);
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Estimates" }), _jsx("p", { style: styles.subheading, children: "Create and manage project estimates" })] }), _jsx("button", { style: styles.primaryButton, onClick: () => navigate('/estimates/new'), children: "+ New Estimate" })] }), _jsx("div", { style: styles.tabsContainer, children: _jsx("div", { style: { ...styles.tabsRow, overflowX: isMobile ? 'auto' : 'visible' }, children: STATUS_TABS.map((tab) => (_jsx("button", { style: {
                                ...styles.tab,
                                ...(statusFilter === tab.value ? styles.tabActive : {}),
                            }, onClick: () => handleTabChange(tab.value), children: tab.label }, tab.value))) }) }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading && !result && (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })), result && result.items.length === 0 && !loading && (_jsx("p", { style: styles.emptyText, children: "No estimates found." })), result && result.items.length > 0 && (_jsx("div", { style: { opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }, children: isMobile ? (_jsx("div", { children: result.items.map((e) => (_jsx(EstimateMobileCard, { estimate: e, onClick: () => navigate(`/estimates/${e.id}`) }, e.id))) })) : (_jsxs("div", { style: styles.listCard, children: [_jsxs("div", { style: styles.tableHeader, children: [_jsx("span", { style: { ...styles.col, flex: 2 }, children: "ID" }), _jsx("span", { style: { ...styles.col, flex: 2.5 }, children: "Customer" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Status" }), _jsx("span", { style: { ...styles.col, flex: 3 }, children: "Description" }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: "Total" }), _jsx("span", { style: { ...styles.col, flex: 2 }, children: "Created" })] }), result.items.map((e, idx) => (_jsx(EstimateRow, { estimate: e, isLast: idx === result.items.length - 1, onClick: () => navigate(`/estimates/${e.id}`) }, e.id)))] })) })), result && (result.items.length > 0 || pageIndex > 0) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: styles.pageButton, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Previous" }), _jsxs("span", { style: styles.pageInfo, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: styles.pageButton, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const EstimateMobileCard = ({ estimate, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const addr = estimate.siteAddress;
    const location = addr ? `${addr.city}, ${addr.state}` : '';
    return (_jsxs("div", { style: {
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : '#fff',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                }, children: [_jsx("span", { style: { fontSize: '16px', fontWeight: '700', color: '#1d1d1f' }, children: estimate.customerName }), _jsx("span", { style: statusBadgeStyle(estimate.status), children: estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1) })] }), _jsx("p", { style: {
                    margin: '4px 0 0 0',
                    fontSize: '14px',
                    color: '#3c3c43',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }, children: estimate.projectDescription }), location && (_jsx("p", { style: { margin: '2px 0 0 0', fontSize: '13px', color: '#86868b' }, children: location })), _jsxs("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '6px',
                }, children: [_jsx("span", { style: { fontSize: '13px', color: '#86868b' }, children: new Date(estimate.createdAt).toLocaleDateString() }), _jsxs("span", { style: { fontSize: '14px', fontWeight: '600', color: '#1d1d1f' }, children: ["$", estimate.totalAmount.toFixed(2)] })] })] }));
};
const EstimateRow = ({ estimate, isLast, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const shortId = estimate.id.length > 8 ? estimate.id.slice(0, 8) + '…' : estimate.id;
    return (_jsxs("div", { style: {
            ...styles.row,
            borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
            backgroundColor: hovered ? 'rgba(0,122,255,0.04)' : 'transparent',
            cursor: 'pointer',
        }, onClick: onClick, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx("span", { style: { ...styles.col, flex: 2, color: '#86868b', fontFamily: 'monospace' }, children: shortId }), _jsx("span", { style: { ...styles.col, flex: 2.5, fontWeight: '600', color: '#1d1d1f' }, children: estimate.customerName }), _jsx("span", { style: { ...styles.col, flex: 1.5 }, children: _jsx("span", { style: statusBadgeStyle(estimate.status), children: estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1) }) }), _jsx("span", { style: {
                    ...styles.col,
                    flex: 3,
                    color: '#3c3c43',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }, children: estimate.projectDescription }), _jsxs("span", { style: { ...styles.col, flex: 1.5, fontWeight: '600', color: '#1d1d1f' }, children: ["$", estimate.totalAmount.toFixed(2)] }), _jsx("span", { style: { ...styles.col, flex: 2, color: '#86868b' }, children: new Date(estimate.createdAt).toLocaleDateString() })] }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '32px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    primaryButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        letterSpacing: '-0.2px',
        whiteSpace: 'nowrap',
        minHeight: '44px',
    },
    tabsContainer: {
        marginBottom: '16px',
    },
    tabsRow: {
        display: 'flex',
        gap: '8px',
        padding: '4px 0',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
    },
    tab: {
        padding: '8px 18px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#3c3c43',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minHeight: '36px',
        transition: 'all 0.15s ease',
    },
    tabActive: {
        backgroundColor: '#0071e3',
        color: '#fff',
        border: '1px solid #0071e3',
        fontWeight: '600',
    },
    errorBanner: {
        padding: '14px 20px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    listCard: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden',
    },
    tableHeader: {
        display: 'flex',
        padding: '12px 24px',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    col: {
        fontSize: '13px',
        color: '#86868b',
        fontWeight: '500',
        letterSpacing: '-0.1px',
        paddingRight: '12px',
    },
    row: {
        display: 'flex',
        padding: '16px 24px',
        alignItems: 'center',
        transition: 'background-color 0.15s ease',
    },
    badgeBlue: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(0,122,255,0.12)',
        color: '#0071e3',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGreen: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(52,199,89,0.12)',
        color: '#248a3d',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeRed: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(255,59,48,0.12)',
        color: '#c0392b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    badgeGray: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: 'rgba(142,142,147,0.15)',
        color: '#86868b',
        borderRadius: '980px',
        fontSize: '12px',
        fontWeight: '600',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyText: {
        textAlign: 'center',
        padding: '60px 24px',
        color: '#86868b',
        fontSize: '16px',
        margin: 0,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '10px 20px',
        backgroundColor: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#1d1d1f',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=EstimateList.js.map